import { CalendarClock, ExternalLink, Play, Zap } from "lucide-react";
import { usePermission } from "@ncp/api-client";
import { formatDateTime, formatRelative, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, EmptyState, InlineAlert, Skeleton, notifySaved } from "@ncp/ui";
import { usePageActions, usePageJobs, type PageDetail, type PageJobSpec } from "../api";

const runTone = (s: string) => (s === "Succeeded" ? "success" : s === "Failed" ? "danger" : s === "Skipped" ? "outline" : "info") as "success" | "danger" | "outline" | "info";

/** What the page runs on its own: each job as page.json declares it, beside the automation it became when published and its latest runs. */
export function JobsTab({ detail, onOpenAutomation }: { detail: PageDetail; onOpenAutomation: (key: string) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = usePermission();
  const jobs = usePageJobs(detail.page.key);
  const actions = usePageActions();
  const specs = detail.jobs ?? [];
  const live = jobs.data ?? [];

  if (specs.length === 0 && live.length === 0) return <EmptyState icon={CalendarClock} title={t("admin:pageStudio.jobs.emptyTitle")} description={t("admin:pageStudio.jobs.emptyBody")} />;

  const run = async (id: string) => {
    try { notifySaved(await actions.runJob.mutateAsync(id), t("admin:pageStudio.jobs.started")); void jobs.refetch(); } catch { /* useSave reported it */ }
  };

  const when = (s: PageJobSpec) => s.schedule ? t("admin:pageStudio.jobs.schedule", { cron: s.schedule }) : s.on ? t("admin:pageStudio.jobs.event", { event: s.on.event }) + [s.on.playbook, s.on.state, s.on.series].filter(Boolean).map((x) => ` · ${x}`).join("") : t("admin:pageStudio.jobs.manual");
  const what = (s: PageJobSpec) => s.run.agent ? t("admin:pageStudio.jobs.runsAgent", { agent: s.run.agent }) : s.run.function ? t("admin:pageStudio.jobs.runsFunction", { name: s.run.function }) : s.run.model ? t("admin:pageStudio.jobs.runsModel", { name: s.run.model }) : "—";

  return (
    <div className="space-y-4">
      {detail.page.status !== "Published" ? <InlineAlert tone="info">{t("admin:pageStudio.jobs.draftNote")}</InlineAlert> : null}
      {jobs.isLoading ? <Skeleton className="h-40" /> : null}
      {specs.map((s) => {
        const job = live.find((j) => j.job === s.key);
        return (
          <Card key={s.key}>
            <CardHeader className="flex-row items-start justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="flex flex-wrap items-center gap-2"><Zap className="size-4 text-aegold-700" /> {(locale === "ar" && s.titleAr) || s.title || s.key} <code className="font-mono text-xs font-normal text-muted-foreground" dir="ltr">{s.key}</code>
                  {job ? <Badge size="sm" variant={job.enabled ? "success" : "outline"}>{job.enabled ? t("admin:common.enabled") : t("admin:common.disabled")}</Badge> : <Badge size="sm" variant="outline">{t("admin:pageStudio.jobs.notLive")}</Badge>}
                  {s.digest ? <Badge size="sm" variant="camel">{t("admin:pageStudio.jobs.digest")}</Badge> : null}
                </CardTitle>
                <CardDescription>{when(s)} · {what(s)}{s.owner ? ` · ${t("admin:pageStudio.jobs.owner", { name: s.owner })}` : job?.owner ? ` · ${t("admin:pageStudio.jobs.owner", { name: job.owner })}` : ""}</CardDescription>
              </div>
              {job ? (
                <div className="flex shrink-0 gap-1">
                  {can("platform.automations.run") ? <Button size="sm" variant="outline" loading={actions.runJob.isPending} onClick={() => void run(job.id)}><Play /> {t("admin:pageStudio.jobs.runNow")}</Button> : null}
                  <Button size="sm" variant="ghost" onClick={() => onOpenAutomation(job.automationKey)}><ExternalLink /> {t("admin:pageStudio.jobs.automation")}</Button>
                </div>
              ) : null}
            </CardHeader>
            <CardContent>
              {s.run.brief ? <p className="mb-2 text-sm text-muted-foreground">{s.run.brief}</p> : null}
              {job ? (
                <>
                  <p className="mb-2 text-xs text-muted-foreground">
                    {job.nextRunAt ? t("admin:pageStudio.jobs.next", { when: formatDateTime(job.nextRunAt, locale) }) : null}
                    {job.lastRunAt ? ` · ${t("admin:pageStudio.jobs.last", { when: formatRelative(job.lastRunAt, locale) })}` : ""}
                    {` · ${t("admin:pageStudio.jobs.counts", { runs: job.runCount, failures: job.failureCount })}`}
                  </p>
                  {job.pausedReason ? <InlineAlert tone="warning" className="mb-2">{job.pausedReason}</InlineAlert> : null}
                  {job.runs.length === 0 ? <p className="text-xs text-muted-foreground">{t("admin:pageStudio.jobs.noRuns")}</p> : (
                    <ul className="divide-y divide-sand-100 text-xs">
                      {job.runs.map((r) => (
                        <li key={r.id} className="flex items-center gap-2 py-1.5">
                          <Badge size="sm" variant={runTone(r.status)}>{t(`admin:pageStudio.jobs.status.${r.status}`, { defaultValue: r.status })}</Badge>
                          <span className="tabular text-muted-foreground">{formatDateTime(r.at, locale)}</span>
                          <span className="text-muted-foreground">{r.triggeredBy ?? r.eventType ?? ""}</span>
                          <span className="ms-auto tabular text-muted-foreground">{(r.durationMs / 1000).toFixed(1)} s</span>
                          {r.error ? <span className="basis-full text-danger-700">{r.error}</span> : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : null}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
